import type { CellObject, Range, WorkBook, WorkSheet } from 'xlsx';
import { PageRenderer, RenderedPage, fetchFile } from './page-renderer';

/** Guards the DOM against a sheet with a stray formatted cell far out at, say, XFD1048576. */
const MAX_ROWS: number = 2000;
const MAX_COLS: number = 100;

/**
 * Excel (`.xlsx`, `.xls`) and `.csv` via SheetJS — one table per worksheet, shown one at a time
 * behind the viewer's sheet tabs.
 *
 * The table is built here with `textContent` rather than SheetJS's `sheet_to_html`, which passes a
 * cell's rich-text HTML through unescaped. Each cell carries its reference in `data-cell`, so a
 * text pin can say which cell it was made in.
 */
export class SheetRenderer implements PageRenderer {

    readonly paged: boolean = true;
    readonly hasText: boolean = true;

    private host: HTMLElement | null = null;
    private body: HTMLElement | null = null;

    async render(src: string, host: HTMLElement): Promise<RenderedPage[]> {
        this.host = host;
        const XLSX = await import('xlsx');
        const data: ArrayBuffer = await fetchFile(src);
        const workbook: WorkBook = XLSX.read(data, { type: 'array', cellText: true });

        const body: HTMLDivElement = document.createElement('div');
        body.className = 'pv-sheets';
        host.appendChild(body);
        this.body = body;

        const pages: RenderedPage[] = workbook.SheetNames.map((name) => {
            const el: HTMLDivElement = document.createElement('div');
            el.className = 'pv-page is-sheet';
            el.appendChild(this.buildTable(XLSX, workbook.Sheets[name]));
            body.appendChild(el);
            return { el, label: `Sheet: ${name}` };
        });

        await this.zoom(100);
        return pages;
    }

    private buildTable(XLSX: typeof import('xlsx'), sheet: WorkSheet): HTMLTableElement {
        const table: HTMLTableElement = document.createElement('table');
        if (!sheet['!ref']) {
            return table;
        }

        const range: Range = XLSX.utils.decode_range(sheet['!ref']);
        const lastRow: number = Math.min(range.e.r, range.s.r + MAX_ROWS - 1);
        const lastCol: number = Math.min(range.e.c, range.s.c + MAX_COLS - 1);
        const merges: Range[] = sheet['!merges'] ?? [];

        // Column letters across the top, as in Excel.
        const head: HTMLTableRowElement = table.createTHead().insertRow();
        head.appendChild(document.createElement('th'));
        for (let col = range.s.c; col <= lastCol; col++) {
            const th: HTMLTableCellElement = document.createElement('th');
            th.textContent = XLSX.utils.encode_col(col);
            head.appendChild(th);
        }

        const tbody: HTMLTableSectionElement = table.createTBody();
        for (let row = range.s.r; row <= lastRow; row++) {
            const tr: HTMLTableRowElement = tbody.insertRow();
            const rowHead: HTMLTableCellElement = document.createElement('th');
            rowHead.textContent = XLSX.utils.encode_row(row);
            tr.appendChild(rowHead);

            for (let col = range.s.c; col <= lastCol; col++) {
                const merge: Range | undefined = merges.find((m) => m.s.r <= row && row <= m.e.r && m.s.c <= col && col <= m.e.c);
                // Covered by a merge that starts elsewhere — the anchor cell spans over it.
                if (merge && (merge.s.r !== row || merge.s.c !== col)) {
                    continue;
                }

                const ref: string = XLSX.utils.encode_cell({ r: row, c: col });
                const cell: CellObject | undefined = sheet[ref];
                const td: HTMLTableCellElement = tr.insertCell();
                td.dataset['cell'] = ref;
                td.textContent = cell ? (cell.w ?? (cell.v != null ? String(cell.v) : '')) : '';
                if (cell?.t === 'n') {
                    td.classList.add('is-number');
                }
                if (merge) {
                    td.rowSpan = merge.e.r - merge.s.r + 1;
                    td.colSpan = merge.e.c - merge.s.c + 1;
                }
            }
        }

        return table;
    }

    async zoom(zoom: number): Promise<void> {
        if (!this.host || !this.body) {
            return;
        }
        // Sheets are wide by nature, so they read at natural size and scroll sideways rather
        // than being fitted to the pane; zoom scales relative to that.
        this.body.style.setProperty('zoom', String(zoom / 100));
    }

    destroy(): void {
        this.body = null;
        this.host = null;
    }
}
